import { graphql } from 'gatsby';
import { ImageElement, RichTextElement } from '@kontent-ai/gatsby-components';
import useLinkProps from '@/components/Link/hooks/useLinkProps';
import { breakpoints } from '@/constants/breakpoints';
import InfoTag from '@/components/InfoTag/InfoTag';

import {
  StyledLinkText,
  StyledShortDescription,
  StyledTag,
  StyledTitle,
  StyledCard,
  StyledContainer,
  StyledImage,
  StyledHeaderContainer,
  StyledRichTextDescription,
} from './Card.styles';
import { isRichTextValid } from '../RichTextBlock/utils';

const IMAGE_WIDTH = 325;
const IMAGE_HEIGHT = 200;
const ICON_WIDTH = 32;
const ICON_HEIGHT = 32;

interface CardProps extends Queries.CARDFragment {
  className?: string;
  onClick?: () => void;
  descriptionLineClamp?: number;
}

const Card = ({ elements, system, className, descriptionLineClamp }: CardProps) => {
  const { headline, image, link, body, body_rich_text, background_color, eyebrow, info_tag, icon } =
    elements;
  const linkElements = link?.value?.linkElements;

  const { linkChildren, linkProps: cardLinkProps } = useLinkProps({
    action: linkElements?.action.value,
    target: linkElements?.target?.value,
    event: linkElements?.event?.value,
  });

  const imageCard = image?.value && {
    ...image.value,
    description: image.value.description ?? `Image for ${headline.value}`,
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
  };
  const iconValue = icon?.value && {
    ...icon.value,
    url: icon.value.url,
    description: icon.value.description ?? '',
    height: ICON_HEIGHT,
    width: ICON_WIDTH,
  };
  const linkText = `${linkElements?.text.value} >`;

  const hasLink = typeof linkElements?.text.value === 'string';
  const hasEyebrow = !!eyebrow?.value;
  const hasInfoTag = !!info_tag?.value;
  const hasBody = typeof body?.value === 'string' && body.value.trim().length > 0;
  const hasRichTextBody = isRichTextValid(body_rich_text?.value);
  const hasHeader = hasInfoTag || !!iconValue;

  return (
    <StyledCard
      data-codename={system.codename}
      {...cardLinkProps}
      className={Array.from(new Set([className, !hasLink && 'not-clickable'])).join(' ')}
      backgroundColor={background_color?.value}
    >
      {linkChildren}
      {imageCard && (
        <StyledImage
          src={imageCard.url}
          alt={imageCard.description}
          sizes={[
            {
              width: 500,
              height: 307.69,
            },
            {
              breakpoint: breakpoints.TABLET,
              width: 720,
              height: 443,
            },
            {
              breakpoint: breakpoints.DESKTOP,
              width: 352,
              height: 217,
            },
          ]}
        />
      )}

      <StyledContainer>
        {hasHeader && (
          <StyledHeaderContainer>
            {iconValue && (
              <ImageElement
                alt={iconValue.description}
                height={iconValue.height}
                width={iconValue.width}
                image={{ ...iconValue, image_id: iconValue.url }}
              />
            )}
            {hasInfoTag && <InfoTag {...info_tag.value} />}
          </StyledHeaderContainer>
        )}
        {hasEyebrow && <StyledTag>{eyebrow.value}</StyledTag>}
        <StyledTitle>{headline.value}</StyledTitle>
        {hasBody && (
          <StyledShortDescription $lineClamp={descriptionLineClamp}>
            {body.value}
          </StyledShortDescription>
        )}
        {hasRichTextBody && (
          <StyledRichTextDescription $lineClamp={descriptionLineClamp}>
            <RichTextElement {...body_rich_text} />
          </StyledRichTextDescription>
        )}
        {hasLink && <StyledLinkText>{linkText}</StyledLinkText>}
      </StyledContainer>
    </StyledCard>
  );
};

export default Card;

export const fragment = graphql`
  fragment CARD on kontent_item_card {
    __typename
    elements {
      headline {
        value
      }
      image {
        value {
          url
          description
        }
      }

      link {
        value {
          ...LINK
        }
      }
      body {
        value
      }
      body_rich_text {
        value
      }
      background_color {
        value
      }
      eyebrow {
        value
      }
      icon {
        value {
          width
          url
          type
          height
          name
          description
        }
      }
      info_tag {
        value {
          ...INFO_TAG
        }
      }
    }
    system {
      codename
      id
    }
  }
`;
